import React, { useCallback, useContext, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import api from '../services/api';
import { Icon } from '../ui';
import { AuthContext } from '../context/AuthContext';

const MAX_HISTORY_TURNS = 20;
// Must match backend's `settings.ai_assistant_max_message_chars` — a higher
// frontend cap would let a message pass client-side validation and then
// 422 on the backend with no indication to the user why.
const MAX_MESSAGE_CHARS = 2000;
export const CHAT_STORAGE_KEY = 'ai_assistant_chat';

function loadStoredMessages() {
  try {
    const raw = sessionStorage.getItem(CHAT_STORAGE_KEY);
    const parsed = raw ? JSON.parse(raw) : [];
    return Array.isArray(parsed) ? parsed.slice(-MAX_HISTORY_TURNS) : [];
  } catch {
    return [];
  }
}

/* Assistant replies arrive as light markdown (**bold**, `code`, - lists,
   ### headings). Rendered with React elements only — never innerHTML — so
   model output can't inject markup. User turns stay plain text. */
function renderInline(text, keyBase) {
  const nodes = [];
  String(text).split(/(`[^`]+`)/g).forEach((seg, i) => {
    if (/^`[^`]+`$/.test(seg)) {
      nodes.push(<code key={`${keyBase}c${i}`}>{seg.slice(1, -1)}</code>);
      return;
    }
    seg.split(/(\*\*[^*]+\*\*)/g).forEach((b, j) => {
      if (/^\*\*[^*]+\*\*$/.test(b)) nodes.push(<strong key={`${keyBase}b${i}-${j}`}>{b.slice(2, -2)}</strong>);
      else if (b) nodes.push(b);
    });
  });
  return nodes;
}

function AssistantMarkdown({ text }) {
  const blocks = [];
  let list = null;
  let listType = null;
  const flush = () => {
    if (list) { blocks.push({ type: listType, items: list }); list = null; listType = null; }
  };
  String(text).split('\n').forEach((line) => {
    const bullet = line.match(/^\s*[-*•]\s+(.*)$/);
    const num = line.match(/^\s*\d+[.)]\s+(.*)$/);
    const heading = line.match(/^\s*#{1,4}\s+(.*)$/);
    if (bullet) {
      if (listType !== 'ul') flush();
      listType = 'ul'; (list = list || []).push(bullet[1]);
    } else if (num) {
      if (listType !== 'ol') flush();
      listType = 'ol'; (list = list || []).push(num[1]);
    } else {
      flush();
      if (heading) blocks.push({ type: 'h', text: heading[1] });
      else if (line.trim()) blocks.push({ type: 'p', text: line });
    }
  });
  flush();
  return (
    <>
      {blocks.map((b, i) => {
        if (b.type === 'ul' || b.type === 'ol') {
          const Tag = b.type;
          return <Tag key={i}>{b.items.map((it, j) => <li key={j}>{renderInline(it, `${i}-${j}`)}</li>)}</Tag>;
        }
        if (b.type === 'h') return <p key={i} className="chat-widget-md-h">{renderInline(b.text, i)}</p>;
        return <p key={i}>{renderInline(b.text, i)}</p>;
      })}
    </>
  );
}

/**
 * Floating corner chat widget — mounted once in Layout.jsx, visible on every
 * authenticated page. Conversation persists across page refreshes via
 * sessionStorage only (cleared on tab close and on logout, never sent
 * anywhere but the existing chat endpoint) — no server-side history.
 */
export default function ChatWidget() {
  const { t, i18n } = useTranslation();
  const { activeOrg } = useContext(AuthContext);
  const [enabled, setEnabled] = useState(false);
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState(loadStoredMessages);
  const [input, setInput] = useState('');
  const [sending, setSending] = useState(false);
  const panelRef = useRef(null);
  const bubbleRef = useRef(null);
  const bodyRef = useRef(null);
  const inputRef = useRef(null);
  const orgIdRef = useRef(activeOrg?.id ?? null);

  // Org switch (topbar) must not leak the previous org's chat turns into the
  // next org's conversation — both on screen and in the `history` array sent
  // back to the backend on the next message.
  useEffect(() => {
    const nextOrgId = activeOrg?.id ?? null;
    if (orgIdRef.current !== nextOrgId) {
      orgIdRef.current = nextOrgId;
      setMessages([]);
      try { sessionStorage.removeItem(CHAT_STORAGE_KEY); } catch { /* ignore */ }
    }
  }, [activeOrg?.id]);

  useEffect(() => {
    let cancelled = false;
    api.get('/api/ai-assistant/status')
      .then((res) => { if (!cancelled) setEnabled(!!res.data?.enabled); })
      .catch(() => { if (!cancelled) setEnabled(false); });
    return () => { cancelled = true; };
  }, [activeOrg?.id]);

  useEffect(() => {
    if (!open) return;
    const onKeyDown = (e) => { if (e.key === 'Escape') setOpen(false); };
    document.addEventListener('keydown', onKeyDown);
    const focusTimer = setTimeout(() => inputRef.current?.focus(), 50);
    return () => {
      document.removeEventListener('keydown', onKeyDown);
      clearTimeout(focusTimer);
    };
  }, [open]);

  useEffect(() => {
    if (bodyRef.current) bodyRef.current.scrollTop = bodyRef.current.scrollHeight;
  }, [messages, sending, open]);

  useEffect(() => {
    try {
      if (messages.length === 0) sessionStorage.removeItem(CHAT_STORAGE_KEY);
      else sessionStorage.setItem(CHAT_STORAGE_KEY, JSON.stringify(messages.slice(-MAX_HISTORY_TURNS)));
    } catch {
      // sessionStorage unavailable (private mode / quota) — chat still works, just won't survive a refresh.
    }
  }, [messages]);

  const sendMessage = useCallback(async (text) => {
    const trimmed = String(text ?? '').trim();
    if (!trimmed || sending) return;

    const userMessage = { role: 'user', content: trimmed.slice(0, MAX_MESSAGE_CHARS), ts: Date.now() };
    const history = messages
      .slice(-MAX_HISTORY_TURNS)
      .map((m) => ({ role: m.role, content: m.content.slice(0, MAX_MESSAGE_CHARS) }));

    setMessages((prev) => [...prev, userMessage]);
    setInput('');
    setSending(true);

    // Snapshot the org this request was asked under — if the user switches
    // org before the response lands, orgIdRef will have moved on (and the
    // switch effect already cleared messages/sessionStorage), so a late
    // reply for the old org must be dropped instead of appended into the
    // new org's conversation.
    const requestOrgId = orgIdRef.current;

    try {
      const res = await api.post('/api/ai-assistant/chat', { message: userMessage.content, history });
      if (orgIdRef.current !== requestOrgId) return;
      const reply = typeof res.data?.reply === 'string' ? res.data.reply : t('aiAssistant.errorMessage');
      setMessages((prev) => [...prev, { role: 'assistant', content: reply, ts: Date.now() }]);
    } catch {
      if (orgIdRef.current !== requestOrgId) return;
      // Never surface raw error/status/detail from the backend — one fixed
      // neutral string regardless of failure type (network, 4xx, 5xx, 429).
      setMessages((prev) => [...prev, { role: 'assistant', content: t('aiAssistant.errorMessage'), ts: Date.now(), isError: true }]);
    } finally {
      setSending(false);
    }
  }, [messages, sending, t]);

  const handleSend = useCallback((e) => {
    e.preventDefault();
    sendMessage(input);
  }, [input, sendMessage]);

  if (!enabled) return null;

  const fmtTime = (ts) => {
    if (!ts) return null;
    try {
      return new Intl.DateTimeFormat(i18n.language || 'en', { hour: '2-digit', minute: '2-digit' }).format(new Date(ts));
    } catch { return null; }
  };

  const suggestions = [t('aiAssistant.suggest1'), t('aiAssistant.suggest2'), t('aiAssistant.suggest3')];

  return (
    <>
      <button
        ref={bubbleRef}
        type="button"
        className="chat-widget-bubble"
        onClick={() => setOpen((o) => !o)}
        aria-label={t('aiAssistant.bubbleLabel')}
        aria-expanded={open}
      >
        <Icon name={open ? 'close' : 'aiCopilot'} size="lg" />
      </button>

      {open && (
        <div
          ref={panelRef}
          className="chat-widget-panel"
          role="dialog"
          aria-modal="false"
          aria-label={t('aiAssistant.panelTitle')}
        >
          <div className="chat-widget-header">
            <div className="chat-widget-header-left">
              <span className="chat-widget-avatar">
                <Icon name="aiCopilot" size="sm" color="var(--accent)" />
              </span>
              <span className="chat-widget-titles">
                <span className="chat-widget-title">{t('aiAssistant.panelTitle')}</span>
                {activeOrg?.name && <span className="chat-widget-scope">{activeOrg.name}</span>}
              </span>
            </div>
            <div className="chat-widget-header-actions">
              {messages.length > 0 && (
                <button
                  type="button"
                  className="chat-widget-hbtn"
                  onClick={() => setMessages([])}
                  title={t('aiAssistant.newChat')}
                  aria-label={t('aiAssistant.newChat')}
                >
                  <Icon name="refresh" size="sm" />
                </button>
              )}
              <button
                type="button"
                className="chat-widget-hbtn"
                onClick={() => setOpen(false)}
                aria-label={t('common.a11y.close')}
              >
                <Icon name="close" size="sm" />
              </button>
            </div>
          </div>

          <div className="chat-widget-body" ref={bodyRef}>
            {messages.length === 0 && !sending && (
              <div className="chat-widget-empty">
                <span className="chat-widget-empty-icon">
                  <Icon name="aiCopilot" size="md" />
                </span>
                <p>{t('aiAssistant.emptyState')}</p>
                <div className="chat-widget-suggestions">
                  {suggestions.map((s) => (
                    <button key={s} type="button" className="chat-widget-suggestion" onClick={() => sendMessage(s)}>
                      {s}
                    </button>
                  ))}
                </div>
              </div>
            )}
            {messages.map((m, i) => (
              <div key={i} className={`chat-widget-turn chat-widget-turn-${m.role}`}>
                <div className="chat-widget-meta">
                  {m.role === 'assistant' && <Icon name="aiCopilot" size="xs" style={{ color: 'var(--accent)' }} />}
                  <span>{m.role === 'assistant' ? t('aiAssistant.panelTitle') : t('aiAssistant.you')}</span>
                  {fmtTime(m.ts) && <span className="chat-widget-time">{fmtTime(m.ts)}</span>}
                </div>
                <div className={`chat-widget-msg chat-widget-msg-${m.role}${m.isError ? ' chat-widget-msg-error' : ''}`}>
                  {m.role === 'assistant' ? <AssistantMarkdown text={m.content} /> : m.content}
                </div>
              </div>
            ))}
            {sending && (
              <div className="chat-widget-turn chat-widget-turn-assistant">
                <div className="chat-widget-meta">
                  <Icon name="aiCopilot" size="xs" style={{ color: 'var(--accent)' }} />
                  <span>{t('aiAssistant.panelTitle')}</span>
                </div>
                <div className="chat-widget-msg chat-widget-msg-assistant chat-widget-typing" aria-label={t('aiAssistant.typing')}>
                  <span className="chat-widget-dot" />
                  <span className="chat-widget-dot" />
                  <span className="chat-widget-dot" />
                </div>
              </div>
            )}
          </div>

          <div className="chat-widget-footer">
            <form className="chat-widget-composer" onSubmit={handleSend}>
              <input
                ref={inputRef}
                type="text"
                className="chat-widget-input"
                value={input}
                onChange={(e) => setInput(e.target.value)}
                placeholder={t('aiAssistant.placeholder')}
                maxLength={MAX_MESSAGE_CHARS}
                disabled={sending}
              />
              <button
                type="submit"
                className="chat-widget-send"
                disabled={sending || !input.trim()}
                aria-label={t('aiAssistant.send')}
              >
                <Icon name="send" size="sm" />
              </button>
            </form>
            <p className="chat-widget-disclaimer">{t('aiAssistant.disclaimer')}</p>
          </div>
        </div>
      )}
    </>
  );
}
